// ---------------------------------------------------------------------------
// ID Card Studio — FieldLibrary (left palette)
// ---------------------------------------------------------------------------
// Two groups: DYNAMIC fields (bind to a person — name, class, roll…) and fixed
// ELEMENTS (static text, photo box, logo image, shape). Every item can be
// dragged onto the card OR clicked to drop at the centre. Drag payload carries
// the element type (and, for fields, the field key) that CardCanvas reads.
// ---------------------------------------------------------------------------
import React from "react";
import {Box, Divider, SimpleGrid, Text, useColorModeValue, VStack} from "@chakra-ui/react";
import {FaFont, FaRegImage, FaRegSquare, FaUserCircle} from "react-icons/fa";
import {ELEMENT_TYPES, FIELD_DEFS} from "../constants/IdCardConstants";

const ELEMENT_TILES = [
    {type: ELEMENT_TYPES.TEXT, label: "Text", Icon: FaFont},
    {type: ELEMENT_TYPES.PHOTO, label: "Photo", Icon: FaUserCircle},
    {type: ELEMENT_TYPES.IMAGE, label: "Logo", Icon: FaRegImage},
    {type: ELEMENT_TYPES.SHAPE, label: "Shape", Icon: FaRegSquare},
];

export default function FieldLibrary({onAddElement, onAddField}) {
    const tileBg = useColorModeValue("gray.50", "gray.700");
    const tileHover = useColorModeValue("blue.50", "gray.600");
    const border = useColorModeValue("gray.200", "gray.600");
    const textColor = useColorModeValue("gray.600", "gray.200");
    const chipBg = useColorModeValue("white", "gray.700");

    return (
        <VStack align="stretch" spacing="12px">
            <Text fontSize="xs" fontWeight="bold" color="gray.400" letterSpacing="0.5px">
                DATA FIELDS
            </Text>
            <VStack align="stretch" spacing="6px">
                {FIELD_DEFS.map((f) => (
                    <Box
                        key={f.key}
                        role="button"
                        draggable
                        onDragStart={(e) => {
                            e.dataTransfer.setData("application/idc-type", ELEMENT_TYPES.FIELD);
                            e.dataTransfer.setData("application/idc-field", f.key);
                        }}
                        onClick={() => onAddField(f.key)}
                        bg={chipBg}
                        border="1px solid"
                        borderColor={border}
                        borderRadius="8px"
                        px="10px"
                        py="7px"
                        cursor="grab"
                        transition="all 0.12s"
                        _hover={{borderColor: "blue.300", bg: tileHover}}
                        _active={{cursor: "grabbing"}}
                    >
                        <Text fontSize="xs" fontWeight="600" color={textColor}>
                            {f.label}
                        </Text>
                        <Text fontSize="10px" color="gray.400" isTruncated>
                            {f.sample}
                        </Text>
                    </Box>
                ))}
            </VStack>

            <Divider borderColor={border} />

            <Text fontSize="xs" fontWeight="bold" color="gray.400" letterSpacing="0.5px">
                ELEMENTS
            </Text>
            <SimpleGrid columns={2} spacing="10px">
                {ELEMENT_TILES.map(({type, label, Icon}) => (
                    <Box
                        key={type}
                        role="button"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData("application/idc-type", type)}
                        onClick={() => onAddElement(type)}
                        bg={tileBg}
                        border="1px solid"
                        borderColor={border}
                        borderRadius="12px"
                        p="12px 8px"
                        textAlign="center"
                        cursor="grab"
                        transition="all 0.15s"
                        _hover={{bg: tileHover, borderColor: "blue.300", transform: "translateY(-2px)"}}
                        _active={{cursor: "grabbing"}}
                    >
                        <Box display="flex" justifyContent="center" mb="6px" color="blue.400">
                            <Icon size={20} />
                        </Box>
                        <Text fontSize="xs" fontWeight="600" color={textColor}>
                            {label}
                        </Text>
                    </Box>
                ))}
            </SimpleGrid>
            <Text fontSize="10px" color="gray.400" lineHeight="1.4">
                Drag onto the card, or click to drop it in the middle.
            </Text>
        </VStack>
    );
}
